import { Box, Grid, Skeleton } from '@mui/material';
import { useTheme } from '@root/context/ThemeContext';

// import CloudProviderBarSkeleton from '@root/components/v2/content/CloudProviderBar/skeleton';
import BasicContentBox from '@root/components/v2/content/BasicContentBox';
import ContentBox from '@root/components/v2/content/ContentBox';
import PageContent from '@root/components/v2/layout/PageContent';
import PageHeaderSkeleton from '@root/components/v2/layout/PageHeader/Skeleton';

export const OpenThreatSkeleton = () => {
  const { isDarkMode } = useTheme();

  return (
    <ContentBox
      title={
        <div className="d-flex items-center gap-2">
          <p style={{ marginBottom: '0px' }}>Open Threats / Anomaly</p>
          <p style={{ marginBottom: '0px', fontSize: '13px', color: '#64748b' }}>by Severity</p>
        </div>
      }
      height="271px"
      titleSize="medium"
      paddingMode="content"
      containerClassName="border-box-no-pad">
      <Box sx={{ height: '100%', width: '100%', padding: '20px 25px' }}>
        <Grid
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '60px 35px',
            justifyContent: 'space-evenly',
          }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '15px', minWidth: 0 }}>
              <Skeleton
                variant="rounded"
                width={5}
                height={47}
                sx={{ flexShrink: 0, borderRadius: '10px', bgcolor: isDarkMode ? '#2d3748' : undefined }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width={34} height={30} sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
                <Skeleton variant="text" width="75%" height={20} sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
              </Box>
            </Box>
          ))}
        </Grid>
      </Box>
    </ContentBox>
  );
};

export const TopTriggeredSkeleton = () => {
  const { isDarkMode } = useTheme();

  const skeletonBaseColor = isDarkMode ? '#2d3748' : '#f0f0f0';
  const skeletonHighlightColor = isDarkMode ? '#4a5568' : '#e0e0e0';

  return (
    <ContentBox
      titleSize="medium"
      title={
        <div className="TitleTab__Container" style={{ padding: '10px' }}>
          <div style={{ height: '100%' }}>Top Triggered TTPs</div>
          <div style={{ height: '100%' }}>Top Suspicious Entities</div>
        </div>
      }
      titleContainerStyle={{
        // border: "0px",
        fontSize: '16px !important',
        color: 'var(--text-primary)',
        // background: '#F7FAFC',
        // backgroundColor: 'var(--keyinsight-box-header-background)',
      }}
      paddingMode="content"
      containerClassName={'border-box-no-pad'}
      height="271px"
      style={{
        width: '100%',
      }}>
      {/* Five 34px rows on the card's 15px padding and 7px row gap. */}
      <Box
        sx={{ width: '100%', height: '100%', padding: '15px', flexDirection: 'column', gap: '7px', display: 'flex' }}>
        {Array.from(new Array(5)).map((_, idx) => (
          <Box key={idx} width={'100%'} height={'34px'} display={'flex'} justifyContent={'space-between'}>
            <Box sx={{ display: 'flex', gap: '8px', width: '100%', marginRight: '8px' }}>
              <Skeleton
                variant={'rectangular'}
                width={'100%'}
                height={'34px'}
                sx={{ borderRadius: '8px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
              />
            </Box>
            <Box>
              <Skeleton
                variant={'rectangular'}
                width={'34px'}
                height={'34px'}
                sx={{ borderRadius: '8px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </ContentBox>
  );
};

// Mirrors `ThreatExposed`: the legend now sits above the plot, flush left, and the
// chart area is 344px inside the card's 0/15/15 padding.
export const ThreatsSkeleton = () => {
  const { isDarkMode } = useTheme();

  const skeletonBaseColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <ContentBox
      titleSize="medium"
      title="Threats - Triggered  vs Resolved"
      titleContainerStyle={{
        fontSize: '16px !important',
        color: 'var(--text-primary)',
      }}
      paddingMode="content"
      containerClassName={'border-box-no-pad'}
      height="306px"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box
        sx={{
          width: '100%',
          height: 'calc(100% - 40px)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          padding: '0px 15px 15px 15px',
        }}>
        <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center', paddingBottom: '10px' }}>
          {Array.from(new Array(2)).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', height: '20px', alignItems: 'center', gap: '5px' }}>
              <Skeleton variant="circular" width="12px" height="12px" sx={{ bgcolor: skeletonBaseColor }} />
              <Skeleton width="140px" height="20px" sx={{ bgcolor: skeletonBaseColor }} />
            </Box>
          ))}
        </Box>
        <Skeleton
          variant="rectangular"
          width="100%"
          sx={{ flex: 1, borderRadius: '8px', bgcolor: skeletonBaseColor }}
        />
      </Box>
    </ContentBox>
  );
};

// Defaults to the Inventory card's own 306px (the dashboard's first-row height) and the 6 rows that fit below its
// 45px header; the Identity cards pass their own height and rows.
export const InventoryComponentSkeleton = ({ title, skeletonList = 6, height = '306px' }) => {
  const { isDarkMode } = useTheme();

  const skeletonBaseColor = isDarkMode ? '#2d3748' : '#f0f0f0';
  const skeletonHighlightColor = isDarkMode ? '#4a5568' : '#e0e0e0';

  return (
    <ContentBox
      titleSize="medium"
      title={title ? title : 'Inventory'}
      titleContainerStyle={{
        // border: "0px",
        fontSize: '16px !important',
        color: 'var(--text-primary)',
        // background: '#F7FAFC',
        // backgroundColor: 'var(--keyinsight-box-header-background)',
      }}
      {...(!title && {
        // The Inventory card keeps a 45px header with the total resource count on the right.
        headerHeight: 45,
        headerEndElement: (
          <Skeleton variant="text" width={40} height={20} sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
        ),
      })}
      paddingMode="content"
      containerClassName={'border-box-no-pad'}
      height={height}
      style={{
        width: '100%',
      }}>
      {title === 'Kubernetes Security Posture' ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            padding: '8px',
            flexDirection: 'column',
            gap: '8px',
            display: 'flex',
          }}>
          <Grid container columnSpacing={2} marginLeft={'0px !important'}>
            {[0, 1, 2].map(idx => (
              <Grid
                item
                key={idx}
                md={3.8}
                sm={6}
                xs={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  marginRight: '4px',
                  padding: '18px',
                  backgroundColor: isDarkMode ? '#1a202c' : '#fff',
                }}>
                <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
                  <Skeleton
                    variant={'rectangular'}
                    width={'20px'}
                    height={'20px'}
                    sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                  />
                  <Skeleton
                    variant={'rectangular'}
                    width={'100%'}
                    height={'16px'}
                    sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
                  <Skeleton
                    variant={'rectangular'}
                    width={'80px'}
                    height={'30px'}
                    sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', width: '100%', flexDirection: 'column' }}>
                  <Skeleton
                    variant={'rectangular'}
                    width={'100%'}
                    height={'12px'}
                    sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                  />
                  <Skeleton
                    variant={'rectangular'}
                    width={'100%'}
                    height={'12px'}
                    sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
          <Grid container columnSpacing={2} marginLeft={'0px !important'}>
            {[0, 1, 2].map(idx => (
              <Grid
                item
                key={idx}
                md={3.8}
                sm={6}
                xs={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '18px',
                  marginRight: '4px',
                  backgroundColor: isDarkMode ? '#1a202c' : '#fff',
                }}>
                <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
                  <Skeleton
                    variant={'rectangular'}
                    width={'20px'}
                    height={'20px'}
                    sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                  />
                  <Skeleton
                    variant={'rectangular'}
                    width={'100%'}
                    height={'16px'}
                    sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
                  <Skeleton
                    variant={'rectangular'}
                    width={'80px'}
                    height={'30px'}
                    sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', width: '100%', flexDirection: 'column' }}>
                  <Skeleton
                    variant={'rectangular'}
                    width={'100%'}
                    height={'12px'}
                    sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                  />
                  <Skeleton
                    variant={'rectangular'}
                    width={'100%'}
                    height={'12px'}
                    sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box
          sx={{ width: '100%', height: '100%', padding: '8px', flexDirection: 'column', gap: '8px', display: 'flex' }}>
          {Array.from(new Array(skeletonList)).map(idx => (
            <Box key={idx} width={'100%'} height={'30px'} display={'flex'} justifyContent={'space-between'}>
              <Box sx={{ display: 'flex', gap: '8px', width: '100%', marginRight: '8px' }}>
                <Skeleton
                  variant={'rectangular'}
                  width={'30px'}
                  height={'30px'}
                  sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                />
                <Skeleton
                  variant={'rectangular'}
                  width={'100%'}
                  height={'30px'}
                  sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                />
              </Box>
              <Box>
                <Skeleton
                  variant={'rectangular'}
                  width={'30px'}
                  height={'30px'}
                  sx={{ borderRadius: '10px', baseColor: skeletonBaseColor, highlightColor: skeletonHighlightColor }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </ContentBox>
  );
};

// ===== SUSPENSE FALLBACK SKELETONS FOR LAZY-LOADED WIDGETS =====

export const SecurityScoreSkeleton = () => {
  const { isDarkMode } = useTheme();
  return (
    <ContentBox
      title="Security Score"
      titleSize="medium"
      height="442px"
      paddingMode="content"
      containerClassName="border-box-no-pad">
      <Box display="flex" flexDirection="column" alignItems="center" height="406px" padding="15px" paddingTop="30px">
        <Box width="100%" display="flex" justifyContent="center" marginBottom="20px">
          <Skeleton
            variant="circular"
            width="165px"
            height="165px"
            sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }}
          />
        </Box>
        <Box flexGrow={1} height="10px" width="100%" />
        <Box width="100%" display="flex" flexDirection="row" justifyContent="space-between" marginBottom="10px">
          <Skeleton width="80px" sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
          <Skeleton width="80px" sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
        </Box>
        <Grid sx={{ width: '100%' }}>
          {Array.from(new Array(4)).map((_, index) => (
            <Box
              key={index}
              width="100%"
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              marginTop={index === 0 ? '0px' : '8px'}>
              <Skeleton width="70px" sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
              <Skeleton width="70px" sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
            </Box>
          ))}
        </Grid>
      </Box>
    </ContentBox>
  );
};

// Mirrors `SecurityScoreStatCard`: borderless card, the 96px score block, the
// "Shield Security Score" row with its external-link icon and the caption below.
export const SecurityScoreStatSkeleton = () => {
  const { isDarkMode } = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        height: '306px',
        padding: '24px',
        borderRadius: '8px',
        backgroundColor: isDarkMode ? 'var(--content-box-background, #1a202c)' : '#ffffff',
      }}>
      {/* Sized to the score's 96px line-height + 20px margin so the card doesn't shift on load. */}
      <Skeleton
        variant="rectangular"
        width={140}
        height={96}
        style={{ transform: 'none' }}
        sx={{ marginBottom: '20px', borderRadius: '8px', bgcolor: isDarkMode ? '#2d3748' : undefined }}
      />
      <Box display="flex" alignItems="center" gap="8px">
        <Skeleton variant="text" width={200} height={26} sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
        <Skeleton variant="circular" width={14} height={14} sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
      </Box>
      <Box display="flex" alignItems="center" marginTop="6px">
        <Skeleton variant="text" width={280} height={18} sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
      </Box>
    </Box>
  );
};

// Mirrors the loading state inside `ComplianceOverview`: four compliance-family
// rows (icon, score, progress bar) — the card no longer shows a donut.
export const ComplianceOverviewSkeleton = () => {
  const { isDarkMode } = useTheme();
  const skeletonBaseColor = isDarkMode ? '#2d3748' : '#f0f0f0';

  return (
    <ContentBox
      title="Compliance"
      titleSize="medium"
      height="306px"
      paddingMode="content"
      containerClassName="border-box-no-pad no_overflow">
      <Box display="flex" flexDirection="column" alignItems="center" height="264px" overflow="hidden" padding="15px">
        <Grid sx={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
          {Array.from(new Array(4)).map((_, index) => (
            <Grid key={index} sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
              <Grid>
                <Box width="100%" display="flex" flexDirection="row" justifyContent="flex-end" marginTop="5px">
                  <Skeleton variant="circular" width="40px" height="40px" sx={{ bgcolor: skeletonBaseColor }} />
                </Box>
              </Grid>
              <Grid sx={{ width: '100%', marginLeft: '10px' }}>
                <Box width="100%" display="flex" flexDirection="row" justifyContent="flex-end">
                  <Skeleton width="30px" sx={{ bgcolor: skeletonBaseColor }} />
                </Box>
                <Box width="100%">
                  <Skeleton width="100%" sx={{ bgcolor: skeletonBaseColor }} />
                </Box>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Box>
    </ContentBox>
  );
};

// Mirrors the loading state inside `LatestPublishedThreats`: three rows on the
// card's own 12px/16px list padding, each a 32px icon, three text lines and a count.
export const LatestPublishedThreatsSkeleton = () => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <ContentBox
      title="Latest Published Vulnerabilities"
      titleSize="medium"
      height="306px"
      padding="0px"
      paddingMode="content"
      containerClassName="border-box-no-pad">
      <Box display="flex" flexDirection="column" height="264px" overflow="hidden" padding="0">
        {Array.from(new Array(3)).map((_, index) => (
          <Box
            key={index}
            display="flex"
            alignItems="flex-start"
            gap="16px"
            padding="12px 16px"
            borderBottom={index < 2 ? `1px solid ${isDarkMode ? '#2d3748' : '#E5E7EB'}` : 'none'}>
            <Skeleton variant="circular" width="32px" height="32px" sx={{ bgcolor: skeletonColor }} />
            <Box flexGrow={1} minWidth={0}>
              <Skeleton width="40%" height="20px" sx={{ marginBottom: '6px', bgcolor: skeletonColor }} />
              <Skeleton width="80%" height="16px" sx={{ marginBottom: '6px', bgcolor: skeletonColor }} />
              <Skeleton width="30%" height="14px" sx={{ bgcolor: skeletonColor }} />
            </Box>
            <Skeleton width="30px" height="20px" sx={{ bgcolor: skeletonColor }} />
          </Box>
        ))}
      </Box>
    </ContentBox>
  );
};

export const RuntimeProtectionSkeleton = () => {
  const { isDarkMode } = useTheme();
  return (
    <ContentBox
      title="Runtime Protection"
      titleSize="medium"
      height="442px"
      paddingMode="content"
      containerClassName="border-box-no-pad">
      <Box display="flex" flexDirection="column" alignItems="center" height="406px" padding="15px">
        <Box width="100%" display="flex" justifyContent="center" marginBottom="20px">
          <Skeleton
            variant="circular"
            width="165px"
            height="165px"
            sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }}
          />
        </Box>
        <Box width="100%" display="flex" flexDirection="column" gap="10px">
          {Array.from(new Array(4)).map((_, index) => (
            <Box key={index} display="flex" justifyContent="space-between">
              <Skeleton width="70px" sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
              <Skeleton width="70px" sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
            </Box>
          ))}
        </Box>
      </Box>
    </ContentBox>
  );
};

// Mirrors `TopRiskRecommendation`: 380px shell with the 60px header (title, subtitle
// and the affected-resources stat) over five compact recommendation rows.
export const TopRiskRecommendationSkeleton = () => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <Box height="380px" width="100%" minWidth={0}>
      <ContentBox
        title=" Top 5 Risk Recommendations"
        titleSize="medium"
        subtitle="Critical security issues requiring immediate attention"
        headerHeight="60px"
        titleContainerStyle={{
          padding: '0px 15px',
          borderRadius: '12px 12px 0 0',
          border: 'none',
        }}
        headerEndElement={
          <Box display="flex" alignItems="flex-end" flexDirection="column" gap="2px">
            <Skeleton variant="text" width={40} height={22} sx={{ bgcolor: skeletonColor }} />
            <Skeleton variant="text" width={120} height={16} sx={{ bgcolor: skeletonColor }} />
          </Box>
        }
        paddingMode="content"
        containerClassName="border-box-no-pad"
        height="100%"
        containerStyle={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
          overflow: 'hidden',
        }}>
        {/* Same rows as the loaded list: icon, title + time, one description line. */}
        <Box display="flex" flexDirection="column" padding="12px 15px">
          {Array.from(new Array(5)).map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                padding: '10px 12px',
                borderBottom: i < 4 ? `1px solid ${isDarkMode ? '#2d3748' : '#E0E0E0'}` : 'none',
              }}>
              <Skeleton
                variant="rectangular"
                width={28}
                height={28}
                sx={{ borderRadius: '8px', flexShrink: 0, bgcolor: skeletonColor }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: '3px' }}>
                  <Skeleton variant="text" width="55%" height={17} sx={{ bgcolor: skeletonColor }} />
                  <Skeleton variant="text" width={36} height={13} sx={{ bgcolor: skeletonColor }} />
                </Box>
                <Skeleton variant="text" width="90%" height={17} sx={{ bgcolor: skeletonColor }} />
              </Box>
            </Box>
          ))}
        </Box>
      </ContentBox>
    </Box>
  );
};

// The card is now a full-width strip of stat tiles that scrolls sideways, so the
// skeleton lays its eight tiles out in the same single row.
export const InternetExposedResourcesSkeleton = () => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <Box height="100%" width="100%" minWidth={0}>
      <ContentBox
        title="Internet Exposed Resources"
        titleSize="medium"
        titleContainerStyle={{
          fontSize: '16px !important',
          color: 'var(--text-primary)',
        }}
        paddingMode="content"
        containerClassName="border-box-no-pad"
        height="100%"
        containerStyle={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            flexGrow: 1,
            display: 'flex',
            gap: '5px',
            overflow: 'hidden',
            padding: '15px',
          }}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Box
              key={index}
              sx={{
                minWidth: '200px',
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '10px 15px',
                flexShrink: 0,
              }}>
              <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: skeletonColor }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width={36} height={25} sx={{ bgcolor: skeletonColor }} />
                <Skeleton variant="text" width="100%" height={18} sx={{ bgcolor: skeletonColor }} />
              </Box>
            </Box>
          ))}
        </Box>
      </ContentBox>
    </Box>
  );
};

// Mirrors the loading state inside the Inventory `ResourceTrend` card, which the
// dashboard renders at 306px: a two-column legend over the trend chart filling the body.
// Risk Trends is the same card, so it passes its own title.
export const ResourceTrendSkeleton = ({ title = 'Resource Trend' }: { title?: string }) => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;
  return (
    <ContentBox
      title={title}
      titleSize="medium"
      height="306px"
      padding="0px"
      titleContainerStyle={{
        fontSize: '16px !important',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        padding: '11.04px 15px',
        margin: '0px',
      }}
      paddingMode="content"
      containerClassName="border-box-no-pad">
      <Box
        display="flex"
        flexDirection="column"
        gap="10px"
        height="calc(100% - 40px)"
        padding="4px 15px 12px"
        boxSizing="border-box">
        <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap="6px 12px">
          {Array.from(new Array(4)).map((_, index) => (
            <Skeleton key={index} width="110px" height="20px" sx={{ bgcolor: skeletonColor }} />
          ))}
        </Box>
        <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: '4px', bgcolor: skeletonColor }} />
      </Box>
    </ContentBox>
  );
};

export const CQShieldPrioritySkeleton = () => {
  const { isDarkMode } = useTheme();
  return (
    <Grid item md={4}>
      <ContentBox
        title="CQ Shield Priority"
        titleSize="medium"
        height="440px"
        paddingMode="content"
        containerClassName="border-box-no-pad">
        <Box display="flex" flexDirection="column" alignItems="center" height="100%" padding="15px">
          <Box display="flex" justifyContent="center" height="170px" marginRight="12px">
            <Skeleton
              variant="circular"
              width="160px"
              height="160px"
              sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }}
            />
          </Box>
          <Box display="flex" flexDirection="column" width="100%" gap="10px">
            <Box display="flex" gap="10px" height="76px" width="100%">
              <Skeleton width="100%" height="120px" sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
              <Skeleton width="100%" height="120px" sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
            </Box>
            <Box display="flex" gap="10px" height="76px" width="100%">
              <Skeleton width="100%" height="120px" sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
              <Skeleton width="100%" height="120px" sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
            </Box>
          </Box>
        </Box>
      </ContentBox>
    </Grid>
  );
};

// `ThreatExposed` renders `ThreatsSkeleton` while it loads, so the lazy fallback
// uses the same one and the card never changes shape between the two states.
export const ThreatExposedSkeleton = () => <ThreatsSkeleton />;

// `RiskTrends` is the Resource Trend card under its own title, so it loads the same way.
export const RiskTrendsSkeleton = () => <ResourceTrendSkeleton title="Risk Trends" />;

export const KubernetesSecurityPostureSkeleton = () => {
  const { isDarkMode } = useTheme();
  return (
    <ContentBox
      title="Kubernetes Security Posture"
      titleSize="medium"
      height="375px"
      paddingMode="content"
      containerClassName="border-box-no-pad">
      <Box sx={{ width: '100%', height: 'calc(100% - 55.5px)', padding: '12px 16px 16px' }}>
        <Grid container spacing={2} sx={{ height: '100%' }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item key={index} xs={12} sm={6} md={4}>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, height: '100%' }}>
                <Skeleton
                  variant="rounded"
                  width={52}
                  height={52}
                  sx={{ flexShrink: 0, borderRadius: '12px', bgcolor: isDarkMode ? '#2d3748' : undefined }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0, flex: 1 }}>
                  <Skeleton
                    variant="text"
                    width={36}
                    height={28}
                    sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }}
                  />
                  <Skeleton
                    variant="text"
                    width="75%"
                    height={18}
                    sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </ContentBox>
  );
};

// Both identity cards render at 375px, matching Kubernetes Security Posture beside them.
export const IdentityHygieneSkeleton = () => (
  <InventoryComponentSkeleton skeletonList={8} height="375px" title="Identity Hygiene" />
);

export const IdentityPostureSkeleton = () => (
  <InventoryComponentSkeleton skeletonList={8} height="375px" title="Identity Posture" />
);

// Mirrors the loading state inside `CompliancePosture`: the 120px gauge over the
// two check-count groups.
export const CompliancePostureSkeleton = () => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <ContentBox
      title="Compliance Posture"
      titleSize="medium"
      height="271px"
      titleContainerStyle={{
        fontSize: '16px !important',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        borderBottom: '0px',
        margin: '0px',
        color: 'var(--text-primary)',
      }}
      paddingMode="content"
      containerClassName="border-box-no-pad">
      <Box position="relative" height="229px">
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="space-between">
          <Box height="100%">
            <Skeleton
              variant="circular"
              height="120px"
              width="120px"
              style={{ transform: 'none' }}
              sx={{ margin: '15px', bgcolor: skeletonColor }}
            />
            <Skeleton style={{ transform: 'none' }} sx={{ bgcolor: skeletonColor }} />
          </Box>

          <Box display="flex" width="100%" justifyContent="space-between" padding="15px">
            <Box display="flex" paddingBottom="15px" gap="15px">
              {Array.from(new Array(2)).map((_, i) => (
                <Skeleton
                  key={i}
                  height={30}
                  width={30}
                  style={{ transform: 'none' }}
                  sx={{ bgcolor: skeletonColor }}
                />
              ))}
            </Box>

            <Box display="flex" paddingBottom="15px" gap="15px">
              {Array.from(new Array(4)).map((_, i) => (
                <Skeleton
                  key={i}
                  height={30}
                  width={30}
                  style={{ transform: 'none' }}
                  sx={{ bgcolor: skeletonColor }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </ContentBox>
  );
};

// The three cards on this row are 314px. Vulnerabilities and Top 5 Resources Reaching
// EOL are card tables (35px header row, 40px rows), so their skeletons follow that grid.
const CardTableSkeleton = ({ rows = 5 }) => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <Box display="flex" flexDirection="column" padding="0px 15px">
      <Box display="flex" alignItems="center" gap="15px" height="35px">
        <Skeleton variant="text" width="45%" height={14} sx={{ bgcolor: skeletonColor }} />
        <Skeleton variant="text" width="25%" height={14} sx={{ bgcolor: skeletonColor }} />
        <Skeleton variant="text" width="20%" height={14} sx={{ bgcolor: skeletonColor }} />
      </Box>
      {Array.from(new Array(rows)).map((_, i) => (
        <Box key={i} display="flex" alignItems="center" gap="15px" height="40px">
          <Skeleton variant="text" width="45%" height={18} sx={{ bgcolor: skeletonColor }} />
          <Skeleton variant="text" width="25%" height={18} sx={{ bgcolor: skeletonColor }} />
          <Skeleton variant="text" width="20%" height={18} sx={{ bgcolor: skeletonColor }} />
        </Box>
      ))}
    </Box>
  );
};

export const VulnerabilitiesSkeleton = () => (
  <ContentBox
    title="Vulnerabilities"
    titleSize="medium"
    height="314px"
    padding="0"
    titleContainerStyle={{
      fontSize: '16px !important',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      margin: '0px',
      color: 'var(--text-primary)',
    }}
    paddingMode="content"
    containerClassName="border-box-no-pad">
    <CardTableSkeleton rows={5} />
  </ContentBox>
);

export const TopResourcesEOLSkeleton = () => (
  <ContentBox
    title="Top 5 Resources Reaching EOL"
    titleSize="medium"
    height="314px"
    padding="0"
    titleContainerStyle={{
      fontSize: '16px !important',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      margin: '0px',
      color: 'var(--text-primary)',
    }}
    paddingMode="content"
    containerClassName="border-box-no-pad">
    <CardTableSkeleton rows={5} />
  </ContentBox>
);

// Mirrors the loading state inside `TopMitreAttack`: four horizontal bars.
export const TopMitreAttackSkeleton = () => {
  const { isDarkMode } = useTheme();
  return (
    <ContentBox
      title="Top 5 Mitre Att&ck"
      titleSize="medium"
      height="314px"
      padding="0px"
      titleContainerStyle={{
        fontSize: '16px !important',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        padding: '11.04px 15px',
        margin: '0px',
      }}
      paddingMode="content"
      containerClassName="border-box-no-pad">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-around"
        padding="15px"
        gap={'8px'}
        width="100%"
        paddingTop="5px">
        {Array.from(new Array(4)).map((_, index) => (
          <Box key={index} width="100%" height="25px">
            <Skeleton
              width="100%"
              height="35px"
              sx={{ borderRadius: '8px', bgcolor: isDarkMode ? '#2d3748' : undefined }}
            />
          </Box>
        ))}
      </Box>
    </ContentBox>
  );
};

// Mirrors `PrioritizationFunnel`: the Attack Path Flow / Recommendation tab bar
// above the Sankey area, which loads as three bands of five columns.
export const PrioritizationFunnelSkeleton = () => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <ContentBox
      title="Prioritization Funnel"
      titleSize="medium"
      height="calc(100% - 50px)"
      padding="0px"
      titleContainerStyle={{
        fontSize: '16px !important',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        padding: '9px',
        margin: '0px',
        color: 'var(--text-primary)',
      }}
      paddingMode="content"
      containerClassName="border-box-no-pad">
      <Box display="flex" gap="30px" alignItems="center" padding="10px 15px">
        <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: skeletonColor }} />
        <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: skeletonColor }} />
      </Box>
      <Box width="100%" height="400px" padding="40px 15px">
        {Array.from(new Array(3)).map((_, index) => (
          <Box key={index} width="100%" display="flex" flexDirection="column" gap="8px" height="100px">
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              marginTop={index === 0 ? '0px' : '10px'}>
              {Array.from(new Array(5)).map((__, column) => (
                <Skeleton key={column} width="300px" height="100px" sx={{ bgcolor: skeletonColor }} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </ContentBox>
  );
};

export const DashboardDropdownSkeleton = () => (
  <Box display="flex" alignItems="center" gap="8px">
    <Skeleton variant="rectangular" width="100px" height="32px" sx={{ borderRadius: '4px' }} />
  </Box>
);

export const ActiveThreatStoriesCardSkeleton = () => {
  const { isDarkMode } = useTheme();

  return (
    <Box
      sx={{
        height: '380px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isDarkMode ? 'var(--content-box-background, #1a202c)' : '#ffffff',
      }}>
      {/* Sized to the count's 75px line-height + 20px margin so the card doesn't shift on load. */}
      <Skeleton
        variant="rectangular"
        width={90}
        height={75}
        style={{ transform: 'none' }}
        sx={{ borderRadius: '8px', marginBottom: '20px', bgcolor: isDarkMode ? '#2d3748' : undefined }}
      />
      <Box display="flex" alignItems="center" gap="8px">
        <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
        <Skeleton variant="text" width={160} height={22} sx={{ bgcolor: isDarkMode ? '#2d3748' : undefined }} />
      </Box>
    </Box>
  );
};

// The funnel card is built on `FunnelChart`/`BasicContentBox`, not ContentBox, so the
// skeleton uses the same shell and inner padding to keep the header and card edges identical.
export const ThreatFunnelSkeleton = () => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <BasicContentBox
      title="Noise to signal threat funnel"
      headerContent={null}
      rightSideContent={<Skeleton variant="text" width={80} height={18} sx={{ bgcolor: skeletonColor }} />}
      style={{ height: '380px', display: 'flex', flexDirection: 'column' }}
      innerStyles={{
        height: 'calc(100% - 40px)',
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 16px 6px',
        backgroundColor: 'var(--content-box-bg, #ffffff)',
        overflow: 'hidden',
      }}>
      {/* Stat row (icon + count + label per stage: Events / Detections / Stories) above the funnel band. */}
      <Box display="flex" gap="12px" paddingBottom="12px" flexShrink={0}>
        {Array.from(new Array(3)).map((_, index) => (
          <Box key={index} flex={1} display="flex" alignItems="center" gap="8px">
            <Skeleton variant="circular" width={15} height={15} sx={{ bgcolor: skeletonColor }} />
            <Skeleton variant="text" width="70%" height={22} sx={{ bgcolor: skeletonColor }} />
          </Box>
        ))}
      </Box>
      {/* The funnel band renders at a fixed 80px, the five 36px story rows start 12px below it. */}
      <Skeleton
        variant="rectangular"
        width="100%"
        height={80}
        sx={{ borderRadius: '2px', flexShrink: 0, bgcolor: skeletonColor }}
      />
      <Box marginTop="12px" flex={1} minHeight={0}>
        {Array.from(new Array(5)).map((_, index) => (
          <Box key={index} display="flex" alignItems="center" gap="8px" padding="6px 0" minHeight="36px">
            <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: skeletonColor }} />
            <Skeleton variant="text" width="75%" height={18} sx={{ bgcolor: skeletonColor }} />
            <Skeleton variant="text" width="10%" height={18} sx={{ bgcolor: skeletonColor }} />
          </Box>
        ))}
      </Box>
    </BasicContentBox>
  );
};

// Page-level fallback for the /dashboard route (see appRoutes). It reuses the
// per-card skeletons in the same grid `DashboardNew` renders, so every card keeps
// its own dimensions, spacing and structure while the page chunk loads.
function DashboardSkeleton() {
  return (
    <PageContent className="PageContent__Skeleton_pt">
      <PageHeaderSkeleton />
      {/* <CloudProviderBarSkeleton /> */}

      <Grid sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {/* 1st row */}
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <SecurityScoreStatSkeleton />
          </Grid>
          <Grid item xs={3}>
            <ComplianceOverviewSkeleton />
          </Grid>
          <Grid item xs={3}>
            <LatestPublishedThreatsSkeleton />
          </Grid>
          <Grid item xs={3}>
            <ResourceTrendSkeleton />
          </Grid>
        </Grid>

        {/* 2nd row */}
        <Grid container direction="row" spacing={2}>
          <Grid item xs={3}>
            <ActiveThreatStoriesCardSkeleton />
          </Grid>
          <Grid item xs={4.5}>
            <ThreatFunnelSkeleton />
          </Grid>
          <Grid item xs={4.5}>
            <TopRiskRecommendationSkeleton />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InternetExposedResourcesSkeleton />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item md={6}>
            <ThreatExposedSkeleton />
          </Grid>
          <Grid item xs={3}>
            <RiskTrendsSkeleton />
          </Grid>
          <Grid item md={3}>
            <InventoryComponentSkeleton />
          </Grid>
        </Grid>

        {/* 3rd row */}
        <Grid container spacing={2}>
          <Grid item md={4.5}>
            <KubernetesSecurityPostureSkeleton />
          </Grid>
          <Grid item md={3}>
            <IdentityHygieneSkeleton />
          </Grid>
          <Grid item md={4.5}>
            <IdentityPostureSkeleton />
          </Grid>
        </Grid>

        {/* 4th row */}
        <Grid container direction="row" spacing={2}>
          <Grid item xs={4}>
            <OpenThreatSkeleton />
          </Grid>
          <Grid item xs={4}>
            <TopTriggeredSkeleton />
          </Grid>
          <Grid item xs={4}>
            <CompliancePostureSkeleton />
          </Grid>
        </Grid>

        {/* 5th row */}
        <Grid container spacing={2}>
          <Grid item md={4}>
            <VulnerabilitiesSkeleton />
          </Grid>
          <Grid item md={4}>
            <TopResourcesEOLSkeleton />
          </Grid>
          <Grid item md={4}>
            <TopMitreAttackSkeleton />
          </Grid>
        </Grid>

        <PrioritizationFunnelSkeleton />
      </Grid>
    </PageContent>
  );
}

export default DashboardSkeleton;
